import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, User, FileText, ChevronRight, Edit2, Trash2, Folder } from 'lucide-react';
import DeveloperProjectsModal from '../components/Modals/DeveloperProjectsModal';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

interface DeveloperData {
    id: string;
    name: string;
    email: string;
    phone: string;
    createdAt: string;
    totalReceived?: number;
}

const Developers: React.FC = () => {
    const { currency } = useAuth();
    const [developers, setDevelopers] = useState<DeveloperData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingDeveloper, setEditingDeveloper] = useState<DeveloperData | null>(null);
    const [selectedDeveloperForProjects, setSelectedDeveloperForProjects] = useState<DeveloperData | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: ''
    });

    const fetchDevelopers = async () => {
        try {
            setIsLoading(true);
            const res = await api.get('/developers');
            setDevelopers(res.data.developers);
        } catch (error) {
            console.error('Error fetching developers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDevelopers();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingDeveloper) {
                await api.put(`/developers/${editingDeveloper.id}`, formData);
            } else {
                await api.post('/developers', formData);
            }
            setShowForm(false);
            setEditingDeveloper(null);
            setFormData({ name: '', email: '', phone: '' });
            fetchDevelopers();
        } catch (error) {
            console.error('Error saving developer:', error);
        }
    };

    const handleEdit = (developer: DeveloperData) => {
        setEditingDeveloper(developer);
        setFormData({
            name: developer.name,
            email: developer.email || '',
            phone: developer.phone || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this developer?')) {
            try {
                await api.delete(`/developers/${id}`);
                fetchDevelopers();
            } catch (error) {
                console.error('Error deleting developer:', error);
            }
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Developers</h1>
                <button
                    onClick={() => {
                        setEditingDeveloper(null);
                        setFormData({ name: '', email: '', phone: '' });
                        setShowForm(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center gap-2"
                >
                    <Plus size={20} />
                    Add Developer
                </button>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">
                            {editingDeveloper ? 'Edit Developer' : 'New Developer'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="text-center py-10">Loading developers...</div>
            ) : developers.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-10 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                        <User size={32} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Developers Yet</h3>
                    <p className="text-gray-500 max-w-sm mx-auto mb-6">
                        Add developers to track their work on your projects and manage their payment installments.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Received</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {developers.map((dev) => (
                                <tr key={dev.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-medium text-lg">
                                                    {dev.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{dev.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{dev.email || '-'}</div>
                                        <div className="text-sm text-gray-500">{dev.phone || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(dev.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {formatCurrency(dev.totalReceived || 0, currency)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => setSelectedDeveloperForProjects(dev)}
                                            className="text-gray-500 hover:text-blue-600 mr-4"
                                            title="View Projects"
                                        >
                                            <Folder size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleEdit(dev)}
                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                            title="Edit"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(dev.id)}
                                            className="text-red-500 hover:text-red-700"
                                            title="Delete"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DeveloperProjectsModal
                isOpen={!!selectedDeveloperForProjects}
                onClose={() => setSelectedDeveloperForProjects(null)}
                developerId={selectedDeveloperForProjects?.id || ''}
                developerName={selectedDeveloperForProjects?.name || ''}
            />
        </div>
    );
};

export default Developers;
